/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import type { ResponseMappingOutcome, ResponseMappingPreview } from '../../../types/response-mapping-preview';

/**
 * **What a response mapping row picked out of the sample on screen** (`#1144`, slice `2b`).
 *
 * The outbound marks read *what will be sent*, and their words — *omitted*, *(empty)*, *apply your
 * change to see what this will send* — are about a wire. Nothing on this surface goes on a wire, and
 * an entry that finds nothing leaves the driver's own value in place rather than omitting anything,
 * so this surface states its own outcomes in its own words (`#1045`: never reuse a name the UI
 * already gives to something else). The `→` keeps its one meaning, *resolves to* (`#1269`).
 *
 * Nothing is resolved here: every outcome and value arrives from the driver's run.
 */
export interface ResolutionStatement {
  /** The continuation of the row's summary, `→ …` included. */
  statement: string;
  /** Per ad, one line each — the hover, and the accessible description. */
  detail: string;
  /** Nothing was written, so the statement is read as a status rather than a value. */
  quiet: boolean;
}

/** How many ads' values the row itself shows before summarising the rest. */
const SHOWN_ON_ROW = 4;

function adLabel(index: number, adId: string | undefined): string {
  return adId ? `Ad ${index + 1} (${adId})` : `Ad ${index + 1}`;
}

function describeOutcome(outcome: ResponseMappingOutcome | undefined, holds: string | null): string {
  if (!outcome) return 'this row did not run';
  switch (outcome.kind) {
    case 'written':
      return holds === outcome.value
        ? outcome.value
        : `${outcome.value}, then replaced by a later step: the ad holds ${holds ?? 'nothing'}`;
    case 'nothing':
      return holds === null
        ? 'found nothing, and the driver has no value of its own here'
        : `found nothing, so the driver's own value stands: ${holds}`;
    case 'cleared':
      return "found nothing, and the driver's value is declined, so the field is empty";
    case 'suppressed':
      return "the expression says it does not apply, so the driver's value stands";
    case 'error':
      return `the expression failed: ${outcome.message}`;
    case 'withheld':
      return 'a mapping cannot write this field, so this row is ignored';
    case 'not-an-ad-field':
      return 'this target is not a field on the ad, so nothing is stored';
  }
}

/** The short form on the row. A written value that a later step replaced carries a `*`. */
function token(outcome: ResponseMappingOutcome | undefined, holds: string | null): string {
  if (!outcome) return '—';
  switch (outcome.kind) {
    case 'written':
      return holds === outcome.value ? outcome.value : `${outcome.value}*`;
    case 'nothing':
      return '—';
    case 'cleared':
      return 'cleared';
    case 'suppressed':
      return 'n/a';
    case 'error':
      return 'error';
    case 'withheld':
      return 'ignored';
    case 'not-an-ad-field':
      return 'not stored';
  }
}

/** When every ad has the same outcome and none wrote anything, one phrase says it. */
const UNIFORM: Record<Exclude<ResponseMappingOutcome['kind'], 'written'>, string> = {
  nothing: 'nothing found',
  cleared: 'cleared',
  suppressed: 'does not apply',
  error: 'error',
  withheld: 'ignored',
  'not-an-ad-field': 'not stored',
};

function joinTokens(tokens: string[]): string {
  const shown = tokens.slice(0, SHOWN_ON_ROW).join(' · ');
  const rest = tokens.length - SHOWN_ON_ROW;
  return rest > 0 ? `${shown} · +${rest} more` : shown;
}

/**
 * The statement for one mapping row, or `undefined` where there is nothing to claim: no preview, a
 * sample that could not be read or held no ads, or a row that ran for no ad.
 */
export function describeRowResolution(
  target: string,
  preview: ResponseMappingPreview | null,
  stale: boolean,
): ResolutionStatement | undefined {
  if (stale) return { statement: ' → …', detail: 'Reading the sample again for your change.', quiet: true };
  if (!preview?.parsed || preview.ads.length === 0) return undefined;

  const perAd = preview.ads.map((ad) => ({ ad, outcome: ad.entries[target], holds: ad.values[target] ?? null }));
  if (perAd.every((p) => !p.outcome)) return undefined;

  const detail = perAd
    .map((p) => `${adLabel(p.ad.index, p.ad.adId)}: ${describeOutcome(p.outcome, p.holds)}`)
    .join('\n');
  const kinds = new Set(perAd.map((p) => p.outcome?.kind));
  const [only] = kinds;
  if (kinds.size === 1 && only && only !== 'written') {
    return { statement: ` → ${UNIFORM[only]}`, detail, quiet: true };
  }
  return {
    statement: ` → ${joinTokens(perAd.map((p) => token(p.outcome, p.holds)))}`,
    detail,
    quiet: !perAd.some((p) => p.outcome?.kind === 'written'),
  };
}

/**
 * The statement for a field the driver fills with no mapping — **what it filled**, per ad. This is
 * the value the ad holds, whatever put it there, so it is phrased as a value and never as a mapping.
 */
export function describeSuppliedValue(
  target: string,
  preview: ResponseMappingPreview | null,
  stale: boolean,
): ResolutionStatement | undefined {
  if (stale) return { statement: ' → …', detail: 'Reading the sample again for your change.', quiet: true };
  if (!preview?.parsed || preview.ads.length === 0) return undefined;

  const held = preview.ads.map((ad) => ({ ad, value: ad.values[target] ?? null }));
  const detail = held
    .map((h) => `${adLabel(h.ad.index, h.ad.adId)}: ${h.value ?? 'nothing — the sample does not carry it'}`)
    .join('\n');
  if (held.every((h) => h.value === null)) return { statement: ' → not in this sample', detail, quiet: true };
  return { statement: ` → ${joinTokens(held.map((h) => h.value ?? '—'))}`, detail, quiet: false };
}
